import * as React from "react";
import { useEffect, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  Stack,
} from "@mui/material";
import { useDataProvider, useNotify, useRedirect } from "react-admin";
import { useQuery } from "@tanstack/react-query";
import { nextRankInfo } from "../points/ranks";
import { getRankBadge } from "../config/rankBadges";
import { useClub } from "../club/ClubContext";

function typeLabel(t: string) {
  switch (t) {
    case "training_complete":
      return "Training abgeschlossen";
    case "streak_day":
      return "Tages-Streak";
    case "record":
      return "Neuer Rekord";
    case "numeric_bonus":
      return "Numeric Bonus";
    case "challenge_complete":
      return "Challenge abgeschlossen";
    default:
      return t;
  }
}

function parseIsoAssumeUtc(iso: string) {
  // Wenn keine Zeitzone enthalten ist, als UTC interpretieren
  const hasTz = /([zZ]|[+\-]\d{2}:\d{2})$/.test(iso);
  return new Date(hasTz ? iso : `${iso}Z`);
}

function fmtWhen(iso?: string | null) {
  if (!iso) return "—";
  try {
    return parseIsoAssumeUtc(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

type ChipSpec = { label: string; variant?: "filled" | "outlined" };

function makeChips(e: any): ChipSpec[] {
  const chips: ChipSpec[] = [];
  const m = e.meta ?? {};
  if (e.points != null) chips.push({ label: `+${e.points}` });
  if (m.training_name) chips.push({ label: m.training_name, variant: "outlined" });
  if (m.template_name) chips.push({ label: m.template_name, variant: "outlined" });
  if (m.challenge_name) chips.push({ label: m.challenge_name, variant: "outlined" });
  if (m.game_type) chips.push({ label: m.game_type === "numeric" ? "Numeric" : m.game_type, variant: "outlined" });
  if (m.score != null) chips.push({ label: `Score ${m.score}`, variant: "outlined" });
  if (m.avg != null) chips.push({ label: `Ø ${Number(m.avg).toFixed(2)}`, variant: "outlined" });

  if (e.type === "record") {
    if (m.score != null && m.prev_best != null) chips.unshift({ label: `Best ${m.prev_best} → ${m.score}` });
    if (m.avg != null && m.best_avg_before != null) {
      chips.unshift({
        label: `Best Ø ${Number(m.best_avg_before).toFixed(2)} → ${Number(m.avg).toFixed(2)}`,
      });
    }
  }
  return chips;
}

export function PointsWidget() {
  const dp = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const { clubId } = useClub();

  const meQuery = useQuery({
    queryKey: ["points", "me", clubId],
    enabled: !!clubId,
    staleTime: 0,
    queryFn: async () => {
      const meRes = await dp.custom(`points/me?club_id=${clubId}`, { method: "GET" });
      return meRes.data ?? null;
    },
  });

  const eventsQuery = useQuery({
    queryKey: ["points", "events", clubId],
    enabled: !!clubId,
    staleTime: 0,
    queryFn: async () => {
      const evRes = await dp.custom(`points/events?club_id=${clubId}&limit=10`, { method: "GET" });
      return evRes.data ?? [];
    },
  });

  useEffect(() => {
    if (meQuery.isError || eventsQuery.isError) {
      notify("Punkte konnten nicht geladen werden", { type: "warning" });
    }
  }, [meQuery.isError, eventsQuery.isError, notify]);

  const me = meQuery.data as any;
  const events = (eventsQuery.data ?? []) as any[];

  const info = useMemo(() => (me ? nextRankInfo(me.total_points) : null), [me]);

  const currentBadge = useMemo(() => (me?.rank ? getRankBadge(me.rank) : null), [me]);
  const nextBadge = useMemo(() => (info?.next ? getRankBadge(info.next) : null), [info]);

  if (!clubId) return null;

  // während Laden: Widget anzeigen (sonst wirkt Reload "kaputt")
  if (meQuery.isLoading || eventsQuery.isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            Punkte
          </Typography>
          <Box sx={{ mt: 1 }}>
            <LinearProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!me || !info) return null;

  const progress =
    info.nextThreshold == null
      ? 100
      : Math.max(0, Math.min(100, (me.total_points / info.nextThreshold) * 100));

  const goToRanks = () => {
    redirect("/ranks");
  };

  return (
    <Card>
      <CardContent>
        {/* HEADER: Rank Badges */}
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          {/* Current Badge */}
          {currentBadge?.icon ? (
            <Box
              component="img"
              src={currentBadge.icon}
              alt={currentBadge.label}
              onClick={goToRanks}
              sx={{
                width: 48,
                height: 48,
                cursor: "pointer",
              }}
              title={`Aktueller Rang: ${currentBadge.label}`}
            />
          ) : null}

          {/* Points + Rank text */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              {me.rank ?? "—"} · {me.total_points ?? 0} Punkte
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {info.next ? `Nächster Rang: ${info.next} bei ${info.nextThreshold} Punkten` : "Max. Rang erreicht"}
            </Typography>

            <Box sx={{ mt: 1 }}>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          </Box>

          {/* Next Badge (greyed) */}
          {nextBadge?.icon && (
            <Box
              component="img"
              src={nextBadge.icon}
              alt={nextBadge.label}
              onClick={goToRanks}
              sx={{
                width: 48,
                height: 48,
                cursor: "pointer",
                opacity: 0.35,
                filter: "grayscale(100%)",
              }}
              title={`Nächster Rang: ${info.next}`}
            />
          )}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Recent Events */}
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
          Letzte Punkte-Events
        </Typography>

        <List dense sx={{ mt: 0 }}>
          {events.map((e) => {
            const chips = makeChips(e);
            return (
              <ListItem
                key={e.id ?? `${e.type}-${e.created_at}`}
                disableGutters
                sx={{ py: 0.75 }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontWeight: 700 }}>{typeLabel(e.type)}</Typography>
                      <Chip size="small" label={`+${e.points}`} />
                    </Box>
                  }
                  secondary={
                    chips.length ? (
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                        <Chip size="small" variant="outlined" label={fmtWhen(e.created_at)} />
                        {chips
                          .filter((c) => c.label && c.label !== `+${e.points}`)
                          .map((c, idx) => (
                            <Chip
                              key={idx}
                              size="small"
                              label={c.label}
                              variant={c.variant ?? "filled"}
                            />
                          ))}
                      </Stack>
                    ) : null
                  }
                  secondaryTypographyProps={{ component: "div" }}
                />
              </ListItem>
            );
          })}
        </List>
      </CardContent>
    </Card>
  );
}
